import { useMemo } from 'react';
import Fuse from 'fuse.js';
import type { Product } from '../types/product';
import { sortProductsByName } from '../utils/sortProducts';
import type { FilterOption, IndividualBundleOption } from '../types/filters';

interface ActiveFilters {
  searchQuery: string | null;
  collection: string[] | null;
  category: string[] | null;
  subCategory: string[] | null;
  individualBundle: ('Individual Course' | 'Bundle/Package')[] | null;
  sort: 'a-z' | 'z-a' | 'default';
}

type FacetKey = 'collection' | 'category' | 'subCategory' | 'individualBundle';

interface AvailableFilters {
  collection: FilterOption[];
  category: FilterOption[];
  subCategory: FilterOption[];
  individualBundle: IndividualBundleOption[];
}

export default function useSearchAndFilter(allProducts: Product[], filters: ActiveFilters) {
  const { searchQuery: query, collection, category, subCategory, individualBundle, sort } = filters;

  return useMemo(() => {
    // Fuzzy search step
    let searchedProducts: Product[] = allProducts;
    if (query && query.trim()) {
      const fuse = new Fuse(allProducts, {
        keys: [
          { name: 'name', weight: 0.6 },
          { name: 'description', weight: 0.2 },
          { name: 'longDescription', weight: 0.2 },
        ],
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.28,
      });
      searchedProducts = fuse.search(query).map((res) => res.item);
    }

    // Generic accessor for facet values
    const getValue = (product: Product, facet: FacetKey): string => {
      switch (facet) {
        case 'collection':
          return product.productCategory?.verticalName ?? '';
        case 'category':
          return product.productCategory?.categoryName ?? '';
        case 'subCategory':
          return product.productCategory?.subCategoryName ?? '';
        case 'individualBundle':
          return product.bundleContents ? 'Bundle/Package' : 'Individual Course';
      }
    };

    // Precompute totals from searchedProducts
    const totals: Record<FacetKey, Map<string, number>> = {
      collection: new Map(),
      category: new Map(),
      subCategory: new Map(),
      individualBundle: new Map([
        ['Individual Course', 0],
        ['Bundle/Package', 0],
      ]),
    };

    for (const p of searchedProducts) {
      (['collection', 'category', 'subCategory'] as FacetKey[]).forEach((facet) => {
        const val = getValue(p, facet);
        if (val) totals[facet].set(val, (totals[facet].get(val) ?? 0) + 1);
      });
      const bundleVal = getValue(p, 'individualBundle') as 'Individual Course' | 'Bundle/Package';
      totals.individualBundle.set(bundleVal, (totals.individualBundle.get(bundleVal) ?? 0) + 1);
    }

    // Apply filters
    const applyFilters = (f: ActiveFilters, products: Product[]) =>
      products.filter((p) => {
        if (f.collection?.length && !f.collection.includes(getValue(p, 'collection'))) return false;
        if (f.category?.length && !f.category.includes(getValue(p, 'category'))) return false;
        if (f.subCategory?.length && !f.subCategory.includes(getValue(p, 'subCategory'))) return false;
        if (
          f.individualBundle?.length &&
          !f.individualBundle.includes(getValue(p, 'individualBundle') as 'Individual Course' | 'Bundle/Package')
        )
          return false;
        return true;
      });

    let filteredProducts = applyFilters(filters, searchedProducts);

    if (sort) {
      filteredProducts = sortProductsByName(filteredProducts, sort) as Product[];
    }

    // Build facet options with totals/count + matching
    const buildFacet = (facet: FacetKey) => {
      // Create an array of all possible facet values
      const values = Array.from(totals[facet].keys());
      // create the filter object with total and matching counts
      const filterObj = values.map((value) => {
        const filtersWithoutFacet = { ...filters, [facet]: undefined };
        const matching = applyFilters(filtersWithoutFacet, searchedProducts).filter(
          (p) => getValue(p, facet) === value
        ).length;
        return { value, total: totals[facet].get(value) ?? 0, matching };
      });
      // Only include facets with total > 0
      // .filter((f) => f.matching > 0);

      // Sort alphabetically and matching > 0
      filterObj.sort((a, b) => {
        const aGroup = a.matching > 0 ? 0 : 1;
        const bGroup = b.matching > 0 ? 0 : 1;
        if (aGroup !== bGroup) return aGroup - bGroup;
        return aGroup === bGroup ? a.value.localeCompare(b.value) : aGroup - bGroup;
      });

      return filterObj;
    };

    const availableFilters: AvailableFilters = {
      collection: buildFacet('collection'),
      category: buildFacet('category'),
      subCategory: buildFacet('subCategory'),
      individualBundle: buildFacet('individualBundle') as IndividualBundleOption[],
    };

    return { results: filteredProducts, availableFilters, searchedProducts };
  }, [allProducts, query, collection, category, subCategory, individualBundle, sort]);
}
